import { Box, Link, Text } from "@chakra-ui/react";
import React from "react";
import { AIValidationResult } from "src/hooks/useDocumentAI";
import { AISuggestion } from "../AISuggestion";
import { getGoogleMapsUrl } from "../utils/addressUtils";

type PersonData =
  | {
      street_address?: string | null;
      street_address_line_2?: string | null;
      city?: string | null;
      state?: string | null;
      zip_code?: string | null;
    }
  | undefined;

type AddressWithAISuggestionProps = {
  person: PersonData;
  bestAddressAI: AIValidationResult | null;
  suggestedAddress: string | null;
  label?: string;
};

/**
 * Displays a person's address with Google Maps link and AI suggestions.
 *
 * @param person - Person data with address fields
 * @param bestAddressAI - Best AI result for address
 * @param suggestedAddress - Suggested address string from AI
 * @param label - Optional label for the address section (defaults to "Address")
 */
export const AddressWithAISuggestion: React.FC<
  AddressWithAISuggestionProps
> = ({ person, bestAddressAI, suggestedAddress, label = "Address" }) => {
  return (
    <Box my={3} textAlign="left">
      <Text fontSize="sm" color="gray.600" mb={1}>
        {label}
      </Text>
      {person?.street_address ? (
        <>
          <Link
            href={getGoogleMapsUrl(
              person.street_address,
              person.street_address_line_2,
              person.city,
              person.state,
              person.zip_code
            )}
            isExternal
            color="primary.600"
            fontSize="md"
            display="block"
          >
            {person.street_address}
            {person.street_address_line_2 &&
              `, ${person.street_address_line_2}`}
            <br />
            {person.city || ""}, {person.state || ""} {person.zip_code || ""}
          </Link>
        </>
      ) : (
        <Text fontSize="sm">No address available</Text>
      )}

      {/* AI Suggestion for address */}
      {(() => {
        if (!bestAddressAI || !suggestedAddress) return null;

        const fieldRelevance =
          bestAddressAI.field_relevance ?? bestAddressAI.certainty ?? 0;
        if (fieldRelevance < 50) return null;

        if (suggestedAddress.trim() === "") return null;

        // Build current address for comparison
        const currentAddress = person?.street_address
          ? [
              person.street_address,
              person.street_address_line_2,
              `${person.city || ""}, ${person.state || ""} ${
                person.zip_code || ""
              }`,
            ]
              .filter(Boolean)
              .join(", ")
          : "";

        return (
          <AISuggestion
            suggestedValue={suggestedAddress}
            currentValue={currentAddress}
            certainty={bestAddressAI.certainty}
            type="suggestion"
            isEditable={false}
            questionType="Address"
          />
        );
      })()}
    </Box>
  );
};
